import type { RefCallback } from "react";

/**
 * 스크롤 등장 효과(시안 site.js 의 `.rv`·`.rv-img`·`.rv-words`·`.rv-fade`).
 *
 * 요소는 `data-reveal="up|img|words|fade"` 를 갖고, 화면에 들어오면 `data-in` 이 붙어 나타나며, 화면 밖으로 완전히
 * 나가면 `data-in` 이 빠져 다시 볼 때 또 나타난다. 상태는 React state 가 아니라 DOM 속성으로 둔다 — 수십 개 요소가
 * 스크롤마다 바뀌어도 다시 렌더하지 않는다. 숨김 스타일은 `<html data-site-motion>` 일 때만 걸린다(siteGlobalStyles).
 */
export const REVEAL_IN_ATTR = "data-in";

const ENTER_ROOT_MARGIN = "0px 0px -7% 0px";
const ENTER_THRESHOLD = 0.05;

let enterObserver: IntersectionObserver | null = null;
let leaveObserver: IntersectionObserver | null = null;

const observers = () => {
  if (!enterObserver || !leaveObserver) {
    enterObserver = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting)
            entry.target.setAttribute(REVEAL_IN_ATTR, "");
        }),
      { rootMargin: ENTER_ROOT_MARGIN, threshold: ENTER_THRESHOLD }
    );
    leaveObserver = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting)
            entry.target.removeAttribute(REVEAL_IN_ATTR);
        }),
      { threshold: 0 }
    );
  }
  return { enter: enterObserver, leave: leaveObserver };
};

export function observeReveal(element: Element): () => void {
  if (typeof IntersectionObserver === "undefined") return () => {};
  const { enter, leave } = observers();
  enter.observe(element);
  leave.observe(element);
  return () => {
    enter.unobserve(element);
    leave.unobserve(element);
  };
}

/** 등장 효과를 붙일 요소의 ref. `data-reveal` 속성은 호출하는 쪽이 함께 단다. */
export const revealRef: RefCallback<Element> = (element) => {
  if (!element) return undefined;
  return observeReveal(element);
};

/**
 * 첫 화면 키프레임 등장(시안 `.phero-in`)을 화면 밖으로 나갔다 돌아오면 처음부터 다시 재생한다:
 * 화면 밖이면 `data-replay` 를 달아 애니메이션을 끄고, 들어오면 떼어 다시 시작하게 한다.
 */
export const REPLAY_ATTR = "data-replay";

let replayObserver: IntersectionObserver | null = null;

export const replayOnEnterRef: RefCallback<Element> = (element) => {
  if (!element || typeof IntersectionObserver === "undefined") return undefined;
  replayObserver ??= new IntersectionObserver(
    (entries) =>
      entries.forEach((entry) => {
        if (!entry.isIntersecting) entry.target.setAttribute(REPLAY_ATTR, "");
        else entry.target.removeAttribute(REPLAY_ATTR);
      }),
    { threshold: 0 }
  );
  const observer = replayObserver;
  observer.observe(element);
  return () => observer.unobserve(element);
};
